import { z } from 'zod';
import { criteriaConditionSchema } from 'modules/jsonRulesCriterias/businessCriteria';
import {
  FinanceProgramRateType,
  FinanceProgramType,
  MarkupType,
  PaymentPeriodTiming,
  SubsidyCapType,
} from '@trnsact/trnsact-shared-types';
import { FinanceProgramFormSteps, MarkupMethods, Structures } from './types';

const financeProgramTierSchema = z.object({
  name: z.string(),
  color: z.string(),
  maxApprovalAmount: z.number().nullable(),
  interestRate: z.number().nullable(),
  terms: z.string().array(),
  remainingLifePercentage: z.number().nullable(),
  tierMaxTerm: z.string().nullable(),
  maxTerm: z.string().nullable(),
  suggestedDownPaymentPercentage: z.number().nullable(),
  advancePayment: z.string().nullable(),
});

export const financeProgramSchema = z.object({
  step: z.nativeEnum(FinanceProgramFormSteps),
  program: z.object({
    id: z.string().nullable(),
    first: z.object({
      details: z.object({
        name: z.string().min(1, { message: 'Required field' }),
        displayName: z.string().min(1, { message: 'Required field' }),
        guidelines: z.any(),
        linkToLenderCreditApp: z.boolean(),
        lenders: z.object({ label: z.string(), value: z.string() }).array(),
        dealers: z.object({ label: z.string(), value: z.string() }).array(),
        notes: z.string(),
      }),
      criteria: z.object({
        criteria: z.object({ label: z.string(), value: z.string() }).nullable(),
        conditions: criteriaConditionSchema.array(),
      }),
      insuranceCoverage: z.object({
        coverageRequirements: z.any(),
        certificateRequirements: z.any(),
        otherInstructions: z.any(),
      }),
      submissionSettings: z.object({ excludedDocuments: z.any({}).array(), autoSubmitUserProfileId: z.string() }),
    }),
    second: z
      .object({
        rateType: z.nativeEnum(FinanceProgramRateType),
        terms: z.array(z.string()),
        rate: z
          .number({ message: 'Rate should be equal or greater than 0' })
          .gte(0, {
            message: 'Rate should be equal or greater than 0',
          })
          .optional()
          .nullable(),
        minimumRequiredDownPayment: z.number({ message: 'Down Payment should be equal or greater than 0' }),
        specifyRates: z
          .object({
            term: z.string(),
            rate: z.number({ message: 'Rate should be equal or greater than 0' }).gte(0, {
              message: 'Rate should be equal or greater than 0',
            }),
          })
          .array(),
        customerRate: z.number().gte(0, {
          message: 'Rate should be equal or greater than 0',
        }),
        isSubsidized: z.boolean(),
        isSpecifyRate: z.boolean(),
        isTiersEnabled: z.boolean(),
        structure: z.nativeEnum(Structures),
        method: z.nativeEnum(MarkupMethods),
        dealerMarkup: z.number({ message: 'Markup should be equal or greater than 0' }).gte(0),
        programType: z.nativeEnum(FinanceProgramType),
        advancePayment: z.string().min(1, { message: 'Required field' }),
        daysToFirstPayment: z.string().min(0).optional().nullable(),
        paymentCalculationMethod: z.nativeEnum(PaymentPeriodTiming),
        residual: z.number({ message: 'Residual should be equal or greater than 0' }),
        isValidityDates: z.boolean(),
        startDate: z.any(),
        endDate: z.any(),
        fee: z
          .object({
            amount: z.number({ message: 'Amount should be equal or greater than 0' }).gte(0, {
              message: 'Amount should be equal or greater than 0',
            }),
            description: z.string(),
            isFinanced: z.boolean(),
            type: z.nativeEnum(MarkupType),
          })
          .array(),
        configureSubsidyCap: z.boolean(),
        commonLenderMarketRate: z.number().optional().nullable(),
        specifyLenderMarketRates: z.object({ rate: z.number(), term: z.number() }).array().optional().nullable(),
        subsidyCapConfiguration: z
          .object({
            subsidyCapType: z.nativeEnum(SubsidyCapType).optional().nullable(),
            lenderMarketRates: z.object({ rate: z.number(), term: z.number() }).array().optional().nullable(),
            lenderName: z.string().optional().nullable(),
            maxSubsidyAmount: z.number().optional().nullable(),
            netInvoicePercentage: z.number().optional().nullable(),
          })
          .optional()
          .nullable(),
        tiers: financeProgramTierSchema.array().optional().nullable(),
      })
      .refine(
        data => {
          if (data.isValidityDates) return !!data.startDate && !!data.endDate;
          return true;
        },
        {
          path: ['startDate', 'endDate'],
          message: 'Required',
        }
      )
      .refine(
        data => {
          if (!data.isTiersEnabled) return data.terms.length > 0;
          return true;
        },
        {
          path: ['terms'],
          message: 'Please, select at least one term',
        }
      )
      .superRefine((val, ctx) => {
        if (
          val.configureSubsidyCap &&
          val.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.FixedAmount &&
          (val.subsidyCapConfiguration.maxSubsidyAmount === null ||
            val.subsidyCapConfiguration.maxSubsidyAmount === undefined)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxSubsidyAmount'],
            message: 'Max Subsidy Amount is required when Subsidy Cap Type is FixedAmount',
          });
        }

        if (
          val.configureSubsidyCap &&
          val.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.NetInvoicePercentage &&
          (val.subsidyCapConfiguration.netInvoicePercentage === null ||
            val.subsidyCapConfiguration.netInvoicePercentage === undefined)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['netInvoicePercentage'],
            message: 'Net Invoice Percentage is required when Subsidy Cap Type is NetInvoicePercentage',
          });
        }
      }),
  }),
});

export type FinanceProgramForm = z.infer<typeof financeProgramSchema>;

export type FinanceProgramTierForm = z.infer<typeof financeProgramTierSchema>;
